// persistentFilters.js
// Utility functions for managing persistent filters across components

/**
 * Get storage key for racer group filters
 * @param {string} userId - User ID (optional, defaults to 'anonymous')
 * @returns {string} Storage key
 */
export const getRacerGroupFiltersStorageKey = (userId) => {
  if (!userId) return "racerGroupFilters:anonymous";
  return `racerGroupFilters:${userId}`;
};

/**
 * Get storage key for track filters
 * @param {string} userId - User ID (optional, defaults to 'anonymous')
 * @returns {string} Storage key
 */
export const getTrackFiltersStorageKey = (userId) => {
  if (!userId) return "trackFilters:anonymous";
  return `trackFilters:${userId}`;
};

/**
 * Get storage key for event filters
 * @param {string} userId - User ID (optional, defaults to 'anonymous')
 * @returns {string} Storage key
 */
export const getEventFiltersStorageKey = (userId) => {
  if (!userId) return "eventFilters:anonymous";
  return `eventFilters:${userId}`;
};

/**
 * Load persistent racer group filters from localStorage
 * @param {string} userId - User ID
 * @returns {string[]} Array of selected group names
 */
export const loadRacerGroupFilters = (userId) => {
  try {
    const key = getRacerGroupFiltersStorageKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.selectedGroupFilters)) {
      return parsed.selectedGroupFilters;
    }
    return [];
  } catch (error) {
    console.error("Failed to load racer group filters:", error);
    return [];
  }
};

/**
 * Save persistent racer group filters to localStorage
 * @param {string} userId - User ID
 * @param {string[]} selectedGroupFilters - Array of selected group names
 */
export const saveRacerGroupFilters = (userId, selectedGroupFilters) => {
  try {
    const key = getRacerGroupFiltersStorageKey(userId);
    const payload = {
      selectedGroupFilters: Array.isArray(selectedGroupFilters) ? selectedGroupFilters : [],
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save racer group filters:", error);
  }
};

/**
 * Load persistent track filters from localStorage
 * @param {string} userId - User ID
 * @returns {string} Selected track filter ('all' or track name)
 */
export const loadTrackFilters = (userId) => {
  try {
    const key = getTrackFiltersStorageKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) return "all";
    const parsed = JSON.parse(raw);
    if (typeof parsed?.selectedTrackFilter === "string") {
      return parsed.selectedTrackFilter;
    }
    return "all";
  } catch (error) {
    console.error("Failed to load track filters:", error);
    return "all";
  }
};

/**
 * Save persistent track filters to localStorage
 * @param {string} userId - User ID
 * @param {string} selectedTrackFilter - Selected track filter ('all' or track name)
 */
export const saveTrackFilters = (userId, selectedTrackFilter) => {
  try {
    const key = getTrackFiltersStorageKey(userId);
    const payload = {
      selectedTrackFilter: typeof selectedTrackFilter === "string" ? selectedTrackFilter : "all",
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.error("Failed to save track filters:", error);
  }
};
